import * as React from "react";
import * as ReactDOM from "react-dom";
import { WifiOff, HelpCircle, Loader2 } from "lucide-react";
import * as s from "./RealtimeIndicator.style.css";

// ─── Constants ─────────────────────────────────────────────────────────────────

const UPDATE_FREQUENCY_MS = 5 * 60 * 1000;
const ONLINE_THRESHOLD_MS = 20 * 60 * 1000;
const STALE_THRESHOLD_MS  = 60 * 60 * 1000;
const SERVICE_INIT_WINDOW =  8 * 60 * 1000;
const INIT_THRESHOLD_MS   = 20 * 60 * 1000;

// ─── Ring geometry (14×14, stroke 2) ───────────────────────────────────────────

const R_SIZE   = 14;
const R_STROKE = 2;
const R_RADIUS = (R_SIZE - R_STROKE) / 2;
const R_CIRC   = 2 * Math.PI * R_RADIUS;

// ─── Status type ───────────────────────────────────────────────────────────────

type RealtimeStatus =
  | 'online'
  | 'stale'
  | 'offline'
  | 'initializing'
  | 'unknown'
  | 'simulationActive'
  | 'simulationPaused';

// ─── Status derivation ─────────────────────────────────────────────────────────

function deriveStatus(
  lastTelemetryAt: string | null | undefined,
  productCreatedAt: string | null | undefined,
  serviceStartedAt?: number,
  isSimulated?: boolean,
  isSimulating?: boolean,
): RealtimeStatus {
  if (isSimulated) return isSimulating ? 'simulationActive' : 'simulationPaused';

  if (!lastTelemetryAt) {
    if (serviceStartedAt && Date.now() - serviceStartedAt < SERVICE_INIT_WINDOW) return 'initializing';
    if (!productCreatedAt) return 'unknown';
    const ageMs = Date.now() - new Date(productCreatedAt).getTime();
    return ageMs < INIT_THRESHOLD_MS ? 'initializing' : 'unknown';
  }
  const ageMs = Date.now() - new Date(lastTelemetryAt).getTime();
  if (ageMs <= ONLINE_THRESHOLD_MS) return 'online';
  if (ageMs <= STALE_THRESHOLD_MS)  return 'stale';
  return 'offline';
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ageMs = Date.now() - new Date(iso).getTime();
  if (ageMs < 0)          return "just now";
  if (ageMs < 60_000)     return "just now";
  if (ageMs < 3_600_000)  return `${Math.floor(ageMs / 60_000)} min ago`;
  if (ageMs < 86_400_000) return `${Math.floor(ageMs / 3_600_000)} hr ago`;
  return `${Math.floor(ageMs / 86_400_000)}d ago`;
}

function formatAbsoluteTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(); } catch { return null; }
}

function formatNextUpdate(lastTelemetryAt: string | null | undefined): string | null {
  if (!lastTelemetryAt) return null;
  const remaining = new Date(lastTelemetryAt).getTime() + UPDATE_FREQUENCY_MS - Date.now();
  if (remaining <= 0) return "any moment";
  if (remaining < 60_000) return `~${Math.ceil(remaining / 1000)}s`;
  return `~${Math.ceil(remaining / 60_000)} min`;
}

// ─── SVG pieces ────────────────────────────────────────────────────────────────

function CountdownRing({ fraction, color, track }: { fraction: number; color: string; track: string }) {
  const offset = R_CIRC * (1 - fraction);
  return (
    <svg
      width={R_SIZE}
      height={R_SIZE}
      viewBox={`0 0 ${R_SIZE} ${R_SIZE}`}
      style={{ transform: "rotate(-90deg)", flexShrink: 0 }}
      aria-hidden="true"
    >
      <circle cx={R_SIZE / 2} cy={R_SIZE / 2} r={R_RADIUS} fill="none" stroke={track} strokeWidth={R_STROKE} />
      <circle
        cx={R_SIZE / 2} cy={R_SIZE / 2} r={R_RADIUS}
        fill="none" stroke={color} strokeWidth={R_STROKE}
        strokeLinecap="round"
        strokeDasharray={R_CIRC}
        strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 1s linear" }}
      />
    </svg>
  );
}

// Custom pause icon — two thin bars with clear gap, readable at 11px
function PauseSvg({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <rect x="5" y="4" width="4" height="16" rx="1" />
      <rect x="15" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface RealtimeIndicatorProps {
  lastTelemetryAt?: string | null;
  productCreatedAt?: string | null;
  serviceStartedAt?: number;
  domain?: string | null;
  isSimulated?: boolean;
  isSimulating?: boolean;
}

// ─── Display maps ──────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<RealtimeStatus, string> = {
  online:           "Real-time data: Online",
  stale:            "Real-time data: Stale",
  offline:          "Real-time data: Offline",
  initializing:     "Real-time data: Initializing",
  unknown:          "Real-time data: Unknown",
  simulationActive: "Real-time data: Simulation Active",
  simulationPaused: "Real-time data: Simulation Paused",
};

const STATUS_DESC: Record<RealtimeStatus, string> = {
  online:           "Device is actively sending real-time data.",
  stale:            "Device has not sent real-time data recently.",
  offline:          "No real-time data received — device may be offline.",
  initializing:     "Waiting for the first real-time data report.",
  unknown:          "Real-time data status unavailable.",
  simulationActive: "Simulation is running and generating real-time data.",
  simulationPaused: "Simulation is paused — start a session to view real-time data.",
};

function badgeLabel(status: RealtimeStatus): string {
  if (status === 'simulationActive') return 'Simulating';
  if (status === 'simulationPaused') return 'Simulation Paused';
  return 'IoT Core';
}

function formatDomain(domain?: string | null): string | null {
  if (!domain) return null;
  return domain.charAt(0).toUpperCase() + domain.slice(1);
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function RealtimeIndicator({
  lastTelemetryAt,
  productCreatedAt,
  serviceStartedAt,
  domain,
  isSimulated,
  isSimulating,
}: RealtimeIndicatorProps) {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  const [ringFraction, setRingFraction] = React.useState(0);
  React.useEffect(() => {
    const update = () => {
      if (!lastTelemetryAt) { setRingFraction(0); return; }
      const elapsed = Date.now() - new Date(lastTelemetryAt).getTime();
      setRingFraction(Math.min(1, Math.max(0, elapsed / UPDATE_FREQUENCY_MS)));
    };
    update();
    const id = setInterval(update, 1_000);
    return () => clearInterval(id);
  }, [lastTelemetryAt]);

  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{ bottom: number; left: number } | null>(null);

  const showTooltip = React.useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({ bottom: window.innerHeight - rect.top + 8, left: rect.left + rect.width / 2 });
    }
    setTooltipVisible(true);
  }, []);

  const hideTooltip = React.useCallback(() => setTooltipVisible(false), []);

  const status = deriveStatus(lastTelemetryAt, productCreatedAt, serviceStartedAt, isSimulated, isSimulating);
  const colors = s.STATUS_COLORS[status];

  // ── Icon ────────────────────────────────────────────────────────────────────
  let icon: React.ReactNode;
  if (status === 'online' || status === 'simulationActive') {
    icon = <CountdownRing fraction={ringFraction} color={colors.ring} track={colors.track} />;
  } else if (status === 'stale') {
    icon = <CountdownRing fraction={1} color={colors.ring} track={colors.track} />;
  } else if (status === 'simulationPaused') {
    icon = <PauseSvg size={11} color={colors.color} />;
  } else if (status === 'offline') {
    icon = <WifiOff size={11} strokeWidth={2.2} />;
  } else if (status === 'initializing') {
    icon = <Loader2 size={11} strokeWidth={2.2} className={s.iconSpin} />;
  } else {
    icon = <HelpCircle size={11} strokeWidth={2.2} />;
  }

  // ── Tooltip ─────────────────────────────────────────────────────────────────
  const relTime = formatRelativeTime(lastTelemetryAt);
  const absTime = formatAbsoluteTime(lastTelemetryAt);
  const nextUpdate = (status === 'online' || status === 'simulationActive')
    ? formatNextUpdate(lastTelemetryAt)
    : null;

  const tooltipEl = tooltipVisible && tooltipPos ? (
    <div
      className={s.tooltipPortal}
      style={{ position: "fixed", bottom: tooltipPos.bottom, left: tooltipPos.left, transform: "translateX(-50%)" }}
      role="tooltip"
    >
      <div className={s.tooltipInner} style={{ position: "relative" }}>
        <span className={s.tooltipTitle}>{STATUS_LABEL[status]}</span>
        {formatDomain(domain) && (
          <span className={s.tooltipRow}>Domain: {formatDomain(domain)}</span>
        )}
        {relTime && absTime && (
          <span className={s.tooltipRow}>Last update: {relTime} ({absTime})</span>
        )}
        {nextUpdate && (
          <span className={s.tooltipRow}>Next anticipated update: {nextUpdate}</span>
        )}
        <span className={s.tooltipDescription}>{STATUS_DESC[status]}</span>
        <div className={s.tooltipCaret} />
      </div>
    </div>
  ) : null;

  const label = badgeLabel(status);

  return (
    <>
      <span
        ref={wrapperRef}
        className={s.badge}
        style={{ background: colors.background, color: colors.color, borderColor: colors.borderColor }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <span className={s.iconSlot}>{icon}</span>
        {label}
      </span>
      {typeof document !== "undefined" &&
        ReactDOM.createPortal(tooltipEl, document.body)}
    </>
  );
}
